import React, { useEffect, useState } from 'react';
import { DiscountSlab } from '../types';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import './Sections.css';

// Discount slab master (Android firebase bean Discount / DiscountRepository):
// each slab maps a bill-total range (start, end) to an automatic discount %.
// The Bill screen applies the matching slab's percent when no manual discount
// or coupon has been entered.
const Discounts: React.FC = () => {
  const { config, categories } = usePos();
  const [slabs, setSlabs] = useState<DiscountSlab[]>([]);
  useEffect(() => {
    db.getDiscountSlabs().then(setSlabs);
  }, []);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [pct, setPct] = useState('');

  const persist = (next: DiscountSlab[]) => {
    // Keep slabs ordered by range start for predictable matching.
    const sorted = [...next].sort((a, b) => a.startAmount - b.startAmount);
    setSlabs(sorted);
    db.saveDiscountSlabs(sorted);
  };

  const add = () => {
    const s = parseFloat(start) || 0;
    const e = parseFloat(end) || 0;
    const p = parseFloat(pct) || 0;
    if (e <= s) return alert('End amount must be greater than start amount.');
    if (p <= 0 || p >= 100) return alert('Discount % must be between 0 and 100.');
    persist([...slabs, { id: db.newId(), startAmount: s, endAmount: e, percentage: p }]);
    setStart('');
    setEnd('');
    setPct('');
  };

  const remove = (id: string) => persist(slabs.filter((s) => s.id !== id));

  const cur = config.currencySymbol;
  const categoriesWithDiscount = categories.filter((c) => (c.discountPercent ?? 0) > 0);

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>Discounts</h1>
      </div>

      <div className="section-card">
        <h2>Bill Amount Slabs (auto discount)</h2>
        <p className="muted">
          When a bill's amount falls inside a slab, its discount % is applied automatically on
          the Bill screen. Entering a manual discount or coupon overrides the slab.
        </p>
        <div className="field-row">
          <div className="field" style={{ maxWidth: 160 }}>
            <label>Start Amount ({cur})</label>
            <input type="number" value={start} onChange={(e) => setStart(e.target.value)} placeholder="0" />
          </div>
          <div className="field" style={{ maxWidth: 160 }}>
            <label>End Amount ({cur})</label>
            <input type="number" value={end} onChange={(e) => setEnd(e.target.value)} placeholder="1000" />
          </div>
          <div className="field" style={{ maxWidth: 120 }}>
            <label>Discount %</label>
            <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="5" />
          </div>
          <div className="field" style={{ alignSelf: 'flex-end' }}>
            <button className="btn-teal" onClick={add}>Add Slab</button>
          </div>
        </div>

        {slabs.length === 0 ? (
          <p className="muted">No slabs yet.</p>
        ) : (
          <table className="section-table" style={{ maxWidth: 560 }}>
            <thead>
              <tr>
                <th className="right">Above ({cur})</th>
                <th className="right">Below ({cur})</th>
                <th className="right">Discount %</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {slabs.map((s) => (
                <tr key={s.id}>
                  <td className="right">{s.startAmount.toFixed(2)}</td>
                  <td className="right">{s.endAmount.toFixed(2)}</td>
                  <td className="right">{s.percentage}%</td>
                  <td>
                    <button className="btn-grey" onClick={() => remove(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="section-card">
        <h2>Category Wise Discount</h2>
        <p className="muted">
          A per-category line discount is set on the <b>Category Master</b> (Discount % field)
          and applies while App Setting → Advance → General → "D - Category Wise Discount" is on.
        </p>
        {categoriesWithDiscount.length === 0 ? (
          <p className="muted">No categories carry a discount yet.</p>
        ) : (
          <table className="section-table" style={{ maxWidth: 420 }}>
            <thead>
              <tr><th>Category</th><th className="right">Discount %</th></tr>
            </thead>
            <tbody>
              {categoriesWithDiscount.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td className="right">{c.discountPercent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Discounts;
